import { useEffect } from "react";

/**
 * First-party web tracking beacon.
 *
 * Batches events client-side (up to 5 per flush) and POSTs to /api/v1/track.
 * Zero PII — a single anonymous visitor_id + session_id, both random UUIDs
 * generated on first visit and stored in localStorage / sessionStorage.
 *
 * Auto-fires page_view on mount. Also flushes on `beforeunload` via
 * navigator.sendBeacon (survives the tab close).
 *
 * Usage: `useWebTracking()` once at the top of any PUBLIC page component
 * (landing, pricing, signup, login). Do NOT use inside doctor/admin/
 * super-admin dashboards.
 *
 * Emit a click event from anywhere via `window.ddiTrackClick("element-id")`.
 */
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const KEY_VISITOR = "ddi_vid";
const KEY_SESSION = "ddi_sid";

function _uid() {
    try { return crypto.randomUUID().replace(/-/g, "").slice(0, 24); }
    catch { return "v" + Math.random().toString(36).slice(2, 14) + Date.now().toString(36); }
}

function _getVisitor() {
    let v = localStorage.getItem(KEY_VISITOR);
    if (!v) { v = _uid(); localStorage.setItem(KEY_VISITOR, v); }
    return v;
}

function _getSession() {
    let s = sessionStorage.getItem(KEY_SESSION);
    if (!s) { s = _uid(); sessionStorage.setItem(KEY_SESSION, s); }
    return s;
}

function _device() {
    const ua = navigator.userAgent || "";
    if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
    if (/iPad|Tablet/i.test(ua)) return "tablet";
    return "desktop";
}

function _utm() {
    const p = new URLSearchParams(window.location.search);
    return {
        utm_source: p.get("utm_source") || undefined,
        utm_medium: p.get("utm_medium") || undefined,
        utm_campaign: p.get("utm_campaign") || undefined,
        utm_content: p.get("utm_content") || undefined,
    };
}

const _q = [];
let _flushTimer = null;

function _enqueue(ev) {
    const base = {
        visitor_id: _getVisitor(),
        session_id: _getSession(),
        device: _device(),
        referrer: document.referrer || undefined,
        ..._utm(),
    };
    _q.push({ ...base, ...ev });
    if (_flushTimer) clearTimeout(_flushTimer);
    _flushTimer = setTimeout(_flush, 800);
    if (_q.length >= 5) _flush();
}

function _flush(useBeacon = false) {
    if (!_q.length) return;
    const batch = _q.splice(0, 20);
    const url = `${API}/api/v1/track`;
    const body = JSON.stringify(batch);
    try {
        if (useBeacon && navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
        } else {
            fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true })
                .catch(() => {}); // silent fail — tracking must never break the page
        }
    } catch { /* swallow */ }
}

// Expose a global click-tracking API so template-rendered SSR pages can call it too.
if (typeof window !== "undefined" && !window.ddiTrackClick) {
    window.ddiTrackClick = (element, meta) => _enqueue({ kind: "click", element, meta });
    window.addEventListener("beforeunload", () => _flush(true));
}

export default function useWebTracking() {
    useEffect(() => {
        // Fire page_view on mount, once per pathname change.
        _enqueue({ kind: "page_view", path: window.location.pathname + window.location.search });
    }, []);
}

export function trackClick(element, meta) {
    _enqueue({ kind: "click", element, meta });
}
